import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { Github } from 'lucide-react';
import { useId, useState } from 'react';

import { AuthLayout } from '~/components/auth/auth-layout.tsx';
import { ButtonAuth } from '~/components/buttons/button-auth.tsx';
import ErrorList from '~/components/form/ErrorList.tsx';
import Field from '~/components/form/Field.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { supabaseClient } from '~/integrations/supabase/base/client.ts';
import SignUpFormSchema from '~/modules/auth/sign-up-form-schema.ts';
import { getAuthPageDataQueryOptions } from '~/server/auth/auth-page-data-query.ts';
import { signUpWithEmailMutation } from '~/server/auth/sign-up-with-email.ts';
import { cn } from '~/utils/cn.ts';

function validateAuthSearch(search: Record<string, unknown>) {
	return {
		redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
	};
}

export const Route = createFileRoute('/auth/signup')({
	component: SignUpPage,
	loader: ({ context }) => context.queryClient.ensureQueryData(getAuthPageDataQueryOptions()),
	validateSearch: validateAuthSearch,
});

function SignUpPage() {
	const t = useTranslations();
	const { data } = useSuspenseQuery(getAuthPageDataQueryOptions());
	const { baseUrl } = data;
	const { redirectTo } = Route.useSearch();
	const signUp = useServerFn(signUpWithEmailMutation);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [emailErrors, setEmailErrors] = useState<string[] | undefined>(undefined);
	const [passwordErrors, setPasswordErrors] = useState<string[] | undefined>(undefined);
	const [formErrors, setFormErrors] = useState<string[] | undefined>(undefined);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const formErrorId = useId();

	async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		const parsed = SignUpFormSchema.safeParse({
			email,
			password,
			redirectTo,
		});

		if (!parsed.success) {
			const fieldErrors = parsed.error.flatten().fieldErrors;
			setEmailErrors(fieldErrors.email);
			setPasswordErrors(fieldErrors.password);
			setFormErrors(fieldErrors.custom);
			return;
		}

		setEmailErrors(undefined);
		setPasswordErrors(undefined);
		setFormErrors(undefined);
		setIsSubmitting(true);

		try {
			const result = await signUp({ data: parsed.data });
			if (result?.status === 'error') {
				setFormErrors(result.formError ? [result.formError] : undefined);
			}
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<AuthLayout>
			<form className="my-2 space-y-6 p-2" onSubmit={handleSubmit}>
				<Field
					classNames={{
						container: 'flex flex-col',
						error: { container: '' },
						input: cn('h-9 w-full rounded-none', 'hover:border-foreground-primary', 'bg-muted'),
						label: 'mb-1 text-muted-foreground block text-sm font-medium',
					}}
					errors={emailErrors}
					inputProps={{
						autoComplete: 'email',
						onChange: (event) => setEmail(event.currentTarget.value),
						type: 'email',
						value: email,
					}}
					labelProps={{ children: t('auth.email') }}
				/>
				<Field
					classNames={{
						container: 'flex flex-col',
						error: { container: '' },
						input: cn('h-9 w-full rounded-none', 'hover:border-foreground-primary', 'bg-muted'),
						label: 'mb-1 text-muted-foreground block text-sm font-medium',
					}}
					errors={passwordErrors}
					inputProps={{
						autoComplete: 'new-password',
						onChange: (event) => setPassword(event.currentTarget.value),
						type: 'password',
						value: password,
					}}
					labelProps={{ children: t('auth.password') }}
				/>
				<ButtonAuth
					dataTestId="create-account"
					disabled={isSubmitting}
					text={!isSubmitting ? t('auth.signUp') : t('auth.signingUp')}
					type="submit"
				/>
				<ErrorList errors={formErrors} id={formErrorId} />
				<div className="flex items-center justify-center">
					<div className="text-center text-foreground text-sm">
						{t('auth.haveAccount')}{' '}
						<Link className="text-primary text-sm hover:underline" search={{ redirectTo }} to="/auth/signin">
							{t('auth.signIn')}
						</Link>
					</div>
				</div>
			</form>
			<div className="mt-6">
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-border border-t" />
					</div>
					<div className="relative flex justify-center text-sm">
						<span className={cn('bg-muted', 'rounded-sm px-2 text-muted-foreground shadow-md')}>
							{t('auth.orContinueWith')}
						</span>
					</div>
				</div>
				<div className="my-6 flex flex-col items-center">
					<button
						className={cn(
							'flex w-9/10 flex-row justify-center border border-border bg-muted p-2 text-muted-foreground shadow-md',
							'hover:bg-accent',
							'focus:bg-accent',
						)}
						onClick={async () =>
							await supabaseClient.auth.signInWithOAuth({
								options: {
									redirectTo: `${baseUrl}/auth/oauth/callback`,
								},
								provider: 'github',
							})
						}
						type="button"
					>
						<Github className="mr-2" size={24} />
						<span className="text-lg">Github</span>
					</button>
				</div>
			</div>
		</AuthLayout>
	);
}
